<script>
  import { createEventDispatcher } from 'svelte';
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import { faTimes } from '@fortawesome/free-solid-svg-icons';

  export let show = false;
  export let sensorInfo = null;
  export let participantTimezone = 'UTC';
  export let firstReadingDate = null;

  const dispatch = createEventDispatcher();

  let targetDateTime = '';

  // Initialize with current date/time when modal opens
  $: if (show && !targetDateTime) {
    const now = new Date();
    // Format to datetime-local format (YYYY-MM-DDTHH:MM)
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    targetDateTime = `${year}-${month}-${day}T${hours}:${minutes}`;
  }

  function handleConfirm() {
    if (!targetDateTime) {
      alert('Please select a target date and time');
      return;
    }

    // Send the datetime-local value as-is (YYYY-MM-DDTHH:MM)
    // The backend will interpret it in the participant's timezone
    dispatch('confirm', {
      targetDateTime: targetDateTime,
      timezone: participantTimezone
    });
    handleClose();
  }

  function handleClose() {
    targetDateTime = '';
    dispatch('close');
  }

  function formatDateTime(dateString) {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString();
  }
</script>

{#if show}
  <div class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50" on:click={handleClose}>
    <div class="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white dark:bg-gray-800" on:click|stopPropagation>
      <!-- Modal Header -->
      <div class="flex justify-between items-center pb-3 border-b dark:border-gray-700">
        <h3 class="text-xl font-semibold text-gray-900 dark:text-white">
          Time Travel Sensor Data
        </h3>
        <button 
          on:click={handleClose}
          class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        >
          <FontAwesomeIcon icon={faTimes} class="text-xl" />
        </button>
      </div>

      <!-- Modal Body -->
      <div class="pt-4 pb-4">
        {#if sensorInfo}
          <div class="mb-6 p-4 bg-stone-100 dark:bg-gray-700 rounded-md">
            <div class="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span class="font-semibold text-gray-700 dark:text-gray-300">Subject ID:</span>
                <span class="ml-2 text-gray-900 dark:text-white">{sensorInfo.subjectId}</span>
              </div>
              <div>
                <span class="font-semibold text-gray-700 dark:text-gray-300">Device ID:</span>
                <span class="ml-2 text-gray-900 dark:text-white">{sensorInfo.deviceId || 'N/A'}</span>
              </div>
              <div>
                <span class="font-semibold text-gray-700 dark:text-gray-300">Activity GUID:</span>
                <span class="ml-2 text-gray-900 dark:text-white font-mono text-xs">{sensorInfo.activityGUID}</span>
              </div>
              <div>
                <span class="font-semibold text-gray-700 dark:text-gray-300">Reading Count:</span>
                <span class="ml-2 text-gray-900 dark:text-white">{sensorInfo.readingCount}</span>
              </div>
            </div>
          </div>

          <div class="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-md border border-blue-200 dark:border-blue-800">
            <div class="text-sm text-blue-900 dark:text-blue-200">
              <p class="font-semibold mb-2">Participant Timezone: {participantTimezone}</p>
              <p class="mb-1">
                <span class="font-semibold">Current First Reading:</span> 
                <span class="ml-2">{formatDateTime(firstReadingDate)}</span>
              </p>
              <p class="text-xs mt-2 text-blue-700 dark:text-blue-300">
                All readings for this sensor will be shifted by the same time distance.
              </p>
            </div>
          </div>

          <div class="mb-4">
            <label for="targetDateTime" class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              New Date/Time for First Reading (in {participantTimezone}):
            </label>
            <input
              id="targetDateTime"
              type="datetime-local"
              bind:value={targetDateTime}
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              required
            />
            <p class="mt-2 text-xs text-gray-500 dark:text-gray-400">
              Select the new date and time for the first reading in the participant's local time zone ({participantTimezone}).
            </p>
          </div>

          <div class="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-md border border-yellow-200 dark:border-yellow-800">
            <p class="text-sm text-yellow-900 dark:text-yellow-200">
              <span class="font-semibold">Warning:</span> This action will modify {sensorInfo.readingCount} glucose readings. 
              A backup will be created in the GlucoseReadingBackup collection before making any changes.
            </p>
          </div>
        {/if}
      </div>

      <!-- Modal Footer -->
      <div class="flex justify-end gap-3 pt-4 border-t dark:border-gray-700">
        <button
          on:click={handleClose}
          class="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-white rounded-md hover:bg-gray-300 dark:hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-400"
        >
          Cancel
        </button>
        <button
          on:click={handleConfirm}
          class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          Confirm Time Travel
        </button>
      </div>
    </div>
  </div>
{/if}

